import {
  ApiProperty,
  ApiPropertyOptional,
  IntersectionType,
} from '@nestjs/swagger';
import { ModelInterface } from '../interfaces/model.interface';
import { CommonEntityDto } from '../../common/dto/common-entity.dto';
import {
  IsBoolean,
  IsInt,
  IsNumber,
  IsOptional,
  IsPositive,
  IsString,
  IsUUID,
  MaxLength,
  Min,
  MinLength,
} from 'class-validator';
import { Exclude, Expose } from 'class-transformer';
import { ArchivableEntityDto } from '../../common/dto/archivable-entity.dto';
import { SubdivisionDto } from '../../subdivision/dto/subdivision.dto';
import { SpModelNameDto } from '../../sp/model-name/dtos/sp-model-name.dto';
import { ModelBoardFloorDto } from '../../model-board-floor/dto/model-board-floor.dto';

@Exclude()
export class ModelDto
  extends IntersectionType(CommonEntityDto, ArchivableEntityDto)
  implements ModelInterface
{
  @ApiProperty({
    title: 'External ID',
    description: 'Identifier manually carried over from external system.',
  })
  @IsString()
  @MinLength(1)
  @MaxLength(100)
  @Expose()
  externalId!: string;

  @ApiProperty({
    title: 'Name',
    description: 'The name of the Model',
  })
  @IsString()
  @MinLength(2)
  @MaxLength(100)
  @Expose()
  name!: string;

  @ApiProperty({
    title: 'Description',
    description: 'The description of the Model',
  })
  @IsString()
  @MinLength(1)
  @MaxLength(500)
  @Expose()
  description!: string;

  @ApiPropertyOptional({
    title: 'Stocking Specs',
    description: 'The stocking specs of the Model',
  })
  @IsOptional()
  @IsString()
  @MinLength(1)
  @MaxLength(500)
  @Expose()
  stockingSpec!: string;

  @ApiProperty({
    title: 'Garage First?',
    description: 'The model requires garage to be done first.',
    default: false,
  })
  @IsBoolean()
  @Expose()
  garageFirst!: boolean;

  @ApiProperty({
    title: 'Labor Only?',
    description: 'The model is for labor only.',
    default: false,
  })
  @IsBoolean()
  @Expose()
  laborOnly!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: true,
    title: 'Active?',
    description: 'True if model is active',
  })
  @IsBoolean()
  @Expose()
  active!: boolean;

  @ApiProperty({
    title: 'Subdivision ID',
    description: 'The ID of the subdivision this model belongs to',
  })
  @IsUUID()
  @Expose()
  subdivisionId!: string;

  @ApiProperty({
    type: () => SubdivisionDto,
    title: 'Subdivision',
    description: 'The associated subdivision this model belongs to',
  })
  @Expose()
  subdivision!: SubdivisionDto;

  @ApiProperty({
    title: 'highCeiling?',
    description: 'HighCeiling.',
    default: false,
  })
  @IsBoolean()
  @Expose()
  highCeiling!: boolean;

  @ApiProperty({
    title: 'Screw Inspection?',
    description: 'Screw Inspection',
    default: false,
  })
  @IsBoolean()
  @Expose()
  screwInspection!: boolean;

  @ApiProperty({
    type: () => [SpModelNameDto],
    title: 'SpModelNames',
    description: 'The list of SP model names associated with this model.',
  })
  @Expose()
  spModelNames!: SpModelNameDto[];

  @ApiProperty({
    type: () => [ModelBoardFloorDto],
    title: 'ModelBoardFloor',
    description: 'The list of board floors associated with this model.',
  })
  @Expose()
  modelBoardFloors!: ModelBoardFloorDto[];

  @ApiProperty({
    title: 'Default Total Square Footage',
    description: 'Total square footage used by default.',
    default: null,
  })
  @IsOptional()
  @IsInt()
  @Min(1)
  @Expose()
  defaultTotalSquareFootage!: number;

  @ApiProperty({
    title: 'Has Trim',
    description: 'Indicates if the model includes trim.',
    default: false,
  })
  @IsBoolean()
  @Expose()
  hasTrim = false;

  @ApiProperty({
    title: 'Has Interior Options',
    description: 'Indicates if interior options are included.',
    default: false,
  })
  @IsBoolean()
  @Expose()
  hasInteriorOptions = false;

  @ApiProperty({
    title: 'Default Interior Option Cost',
    description: 'Default cost for interior options.',
    default: null,
  })
  @IsOptional()
  @IsNumber({ maxDecimalPlaces: 2 })
  @Expose()
  defaultInteriorOptionCost!: number;

  @ApiProperty({
    title: 'Has Exterior Options',
    description: 'Indicates if exterior options are included.',
    default: false,
  })
  @IsBoolean()
  @Expose()
  hasExteriorOptions = false;

  @ApiProperty({
    title: 'Default Exterior Option Cost',
    description: 'Default cost for exterior options.',
    default: null,
  })
  @IsOptional()
  @IsNumber({ maxDecimalPlaces: 2 })
  @Expose()
  defaultExteriorOptionCost!: number;

  @ApiProperty({
    title: 'Has Trim Options',
    description: 'Indicates if trim options are included.',
    default: false,
  })
  @IsBoolean()
  @Expose()
  hasTrimOptions = false;

  @ApiProperty({
    title: 'Default Trim Option Cost',
    description: 'Default cost for trim options.',
    default: null,
  })
  @IsOptional()
  @IsNumber({ maxDecimalPlaces: 2 })
  @IsPositive()
  @Expose()
  defaultTrimOptionCost!: number;

  @ApiProperty({
    title: 'Has Exterior Paint',
    description:
      'Templates the Exterior One and Exterior Two workflow stages for jobs created from this model (Drywall and Paint). Not the Exterior Paint Options service.',
    default: false,
  })
  @IsBoolean()
  @Expose()
  hasExteriorPaint = false;

  @ApiProperty({
    title: 'Default Exterior Siding/Paint Amount',
    description:
      'Default lump sum, in dollars with cents, for the Exterior Siding/Paint amount of jobs created from this model. Cleared while Exterior Paint is off.',
    default: null,
  })
  @IsOptional()
  @IsNumber({ maxDecimalPlaces: 2 })
  @IsPositive()
  @Expose()
  defaultExteriorSidingPaintAmount!: number;
}
